import { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';

export function CookieNotice({ onAccept }: { onAccept: () => void }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAccepted = localStorage.getItem('cookie-consent');
    if (!hasAccepted) {
      // Small delay to ensure smooth entrance animation
      setTimeout(() => setIsVisible(true), 100);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'true');
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onAccept();
    }, 300);
  };

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 transition-all duration-300 ease-out ${
        isAnimatingOut ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
      }`}
    >
      <div className="max-w-sm sm:max-w-4xl mx-auto">
        <div className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-xl shadow-lg dark:shadow-2xl overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent dark:via-white/2"></div>

          <div className="relative p-4 sm:p-6">
            {/* Mobile Layout */}
            <div className="sm:hidden">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#E34290]/10 dark:bg-[#E34290]/20 flex items-center justify-center">
                    <Cookie className="w-3 h-3 text-[#E34290]" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    We value your privacy
                  </h3>
                </div>
                <button
                  onClick={handleDismiss}
                  className="inline-flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  data-ph-capture-attribute="btn-cookie-notice-close"
                  aria-label="Dismiss"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
                We use cookies to improve your experience and analyze our traffic.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleAccept}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-[#E34290] hover:bg-[#E34290]/90 focus:bg-[#E34290]/90 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#E34290]/20"
                  data-ph-capture-attribute="btn-cookie-notice-accept"
                >
                  Accept
                </button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:block">
              <div className="flex items-start gap-4">
                {/* Cookie Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-10 h-10 rounded-full bg-[#E34290]/10 dark:bg-[#E34290]/20 flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-[#E34290]" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        We value your privacy
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        We use cookies to analyze our traffic and improve your experience. Your data
                        helps us enhance our platform and provide better insights.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        onClick={handleAccept}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#E34290] hover:bg-[#E34290]/90 focus:bg-[#E34290]/90 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#E34290]/20 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                        data-ph-capture-attribute="btn-cookie-notice-accept"
                      >
                        Accept
                      </button>

                      <button
                        onClick={handleDismiss}
                        className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700"
                        data-ph-capture-attribute="btn-cookie-notice-close"
                        aria-label="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
